/**
 * The program's baselines, and the control that cuts one - issue #97, phases B
 * and C.
 *
 * A BASELINE IS A PROTECTED TAG cut from the default branch. It belongs to the
 * program, not to the work branch the rest of the Version Control screen is
 * about, which is why this is a section of its own rather than part of the
 * merge request card.
 *
 * BEST READS AND FORWARDS; GITLAB ENFORCES (decision 1). Who may cut a baseline
 * is whatever the program's protected-tag rule says, so the button is offered to
 * any signed-in person who can reach the program and a refusal is GitLab's own
 * words in a toast - never "sign in", which the server avoids by answering a
 * refusal with 403 and this client reads only a 401 as an expired session. It
 * is hidden only for somebody anonymous or in a single-database run, as the
 * element-comment box is (`canCut`, from the screen): there is no repository to
 * tag, and the click would answer 401.
 *
 * EVERYTHING FROM THE SERVER IS TEXT. A tag name, its notes and a job's name are
 * typed by people or by CI; none is parsed as markup, and a link is drawn only
 * for an http(s) address (`safeHref`) so a `javascript:` URL in a payload is
 * shown as text and not made clickable.
 *
 * Artifacts are read when a row is first expanded and kept: GitLab is asked per
 * tag, and a long list must not cost a call per row. The links go to GitLab's
 * own browse and download pages rather than through BEST (everyone signs in
 * through GitLab already).
 *
 * PHASE D, OPEN (READ-ONLY): each row opens its baseline into a read-only copy and
 * makes it the person's current view. Opening is slow, so the status is followed
 * (see useOpenBaseline) and shown on the row; a failure says why and points at the
 * artifacts, which do not depend on it. PHASE E, COMPARE: each row can start a
 * comparison of the default branch and any baselines (BaselineCompare), drawn below
 * the section with the Version Control screen's own list and export.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { api } from '../../api'
import type { ApiError } from '../../api'
import type {
  Baseline,
  BaselineArtifacts,
  BaselineList,
  BaselineMainMoved,
} from '../../schema'
import { baselineTag } from '../../state/baselineRef'
import { useProject } from '../../state/Project'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import BaselineCompare from './BaselineCompare'
import BaselineOpening from './BaselineOpening'
import { useBaselineView } from './useBaselineView'
import { useOpenBaseline } from './useOpenBaseline'
import type { OpeningBaselines } from './useOpenBaseline'

/** The notes become a tag message: a paragraph or two, not a document. */
const MAX_NOTES = 2000

/** The address if it is http(s), else undefined: only those become links. */
function safeHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? url : undefined
  } catch {
    return undefined
  }
}

/** The date part of an ISO time, or the text as given when it is not one. */
function dayOf(time: string): string {
  return /^\d{4}-\d{2}-\d{2}/.test(time) ? time.slice(0, 10) : time
}

/** A size the way a person reads it. */
function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** An http(s) address as a new-tab link, anything else as the plain text. */
function ExternalLink({ href, children }: { href: string | undefined; children: string }) {
  const safe = safeHref(href)
  if (!safe) return <span>{children}</span>
  return (
    <Link href={safe} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  )
}

function ArtifactsPanel({ found }: { found: BaselineArtifacts }) {
  if (!found.pipeline) {
    return (
      <Typography variant="body2" color="text.secondary">
        No pipeline ran for this baseline (the program&apos;s CI may not publish artifacts yet).
      </Typography>
    )
  }
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2">Pipeline</Typography>
        <Chip size="small" label={found.pipeline.status} />
        <ExternalLink href={found.pipeline.web_url}>{`#${found.pipeline.id} on GitLab`}</ExternalLink>
      </Stack>
      {found.jobs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          The pipeline has no jobs.
        </Typography>
      ) : (
        found.jobs.map((job) => (
          <Box key={job.id} sx={{ pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {job.name}
              </Typography>
              <Chip size="small" variant="outlined" label={job.status} />
              {job.expired ? (
                <Typography variant="caption" color="text.secondary">
                  Artifacts expired
                </Typography>
              ) : job.artifacts.length > 0 ? (
                <>
                  <ExternalLink href={job.browse_url}>Browse</ExternalLink>
                  <ExternalLink href={job.download_url}>Download</ExternalLink>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No artifacts
                </Typography>
              )}
              <ExternalLink href={job.web_url}>Job on GitLab</ExternalLink>
            </Stack>
            {!job.expired && job.artifacts.length > 0 ? (
              <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
                {job.artifacts.map((file) => (
                  <li key={file.filename}>
                    <Typography variant="caption" sx={{ fontFamily: MONOSPACE }}>
                      {file.filename}
                    </Typography>{' '}
                    <Typography variant="caption" color="text.secondary">
                      {formatSize(file.size)}
                    </Typography>
                  </li>
                ))}
              </Stack>
            ) : null}
          </Box>
        ))
      )}
    </Stack>
  )
}

function BaselineRow({
  slug,
  baseline,
  viewing,
  opening,
  onView,
  onCompare,
}: {
  slug: string
  baseline: Baseline
  /** This baseline is the person's current view. */
  viewing: boolean
  opening: OpeningBaselines
  /** Open it if need be and make it the current view. */
  onView: (tag: string) => void
  onCompare: (tag: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [found, setFound] = useState<BaselineArtifacts | null>(null)
  const [failure, setFailure] = useState<ApiError | null>(null)
  const [loading, setLoading] = useState(false)
  // Whether a read is on its way, apart from state: a quick second click must
  // not start a second one.
  const reading = useRef(false)

  const read = useCallback(async () => {
    if (reading.current) return
    reading.current = true
    setLoading(true)
    setFailure(null)
    try {
      setFound(await api.baselineArtifacts(slug, baseline.name))
    } catch (refused) {
      setFailure(refused as ApiError)
    } finally {
      reading.current = false
      setLoading(false)
    }
  }, [slug, baseline.name])

  const toggle = () => {
    const next = !expanded
    setExpanded(next)
    // Once: re-expanding shows what was read. A failed read is retried.
    if (next && !found) void read()
  }

  const builtWith = baseline.trailers['BEST-Version']
  const status = opening.statuses[baseline.name]
  const isOpening = status?.state === 'opening' || status?.state === 'not_opened'
  const ready = baseline.opened || status?.state === 'ready'

  return (
    <Box sx={{ py: 1.25 }} data-testid="baseline-row">
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'baseline', flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <Typography sx={{ fontFamily: MONOSPACE, fontWeight: 600 }}>{baseline.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {dayOf(baseline.committed_at)}
          {baseline.author ? ` by ${baseline.author}` : ''}
        </Typography>
        {builtWith ? (
          <Typography variant="caption" color="text.secondary">
            cut under BEST {builtWith}
          </Typography>
        ) : null}
        <ExternalLink href={baseline.web_url}>Tag on GitLab</ExternalLink>
        <Button
          size="small"
          onClick={toggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Hide' : 'Show'} artifacts of ${baseline.name}`}
          sx={{ minWidth: 0, py: 0 }}
        >
          {expanded ? 'Hide artifacts' : 'Artifacts'}
        </Button>
        {viewing ? (
          <Chip size="small" color="warning" label="Viewing" />
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onView(baseline.name)}
            disabled={isOpening}
            aria-label={`${ready ? 'View' : 'Open'} ${baseline.name}${ready ? '' : ' (read-only)'}`}
            sx={{ minWidth: 0, py: 0 }}
          >
            {isOpening ? 'Opening…' : ready ? 'View' : 'Open (read-only)'}
          </Button>
        )}
        <Button
          size="small"
          onClick={() => onCompare(baseline.name)}
          aria-label={`Compare ${baseline.name}`}
          sx={{ minWidth: 0, py: 0 }}
        >
          Compare
        </Button>
      </Stack>
      {status && status.state !== 'ready' ? <BaselineOpening status={status} /> : null}
      {baseline.notes ? (
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {baseline.notes}
        </Typography>
      ) : null}
      {expanded ? (
        <Box sx={{ mt: 1, pl: 1 }}>
          {loading ? <CircularProgress size={16} aria-label="Loading the artifacts" /> : null}
          {failure ? (
            <Alert severity="warning">Could not read the artifacts: {failure.message}</Alert>
          ) : null}
          {found ? <ArtifactsPanel found={found} /> : null}
        </Box>
      ) : null}
    </Box>
  )
}

/** First seven characters of a sha, which is what a person recognises. */
const short = (sha: string) => sha.slice(0, 7)

export default function BaselinesSection({
  slug,
  target,
  canCut,
}: {
  slug: string
  /** The default branch's name, which is the only thing a baseline is cut from. */
  target: string
  /** False for somebody anonymous or in a single-database run: no repository to tag. */
  canCut: boolean
}) {
  const toasts = useToasts()
  const { branch } = useProject()
  const baselineView = useBaselineView()
  const opening = useOpenBaseline(slug)
  const [list, setList] = useState<BaselineList | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [loading, setLoading] = useState(true)

  const [cutting, setCutting] = useState(false)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  // Set when main has moved since the person's copy was imported: they choose.
  const [moved, setMoved] = useState<BaselineMainMoved | null>(null)
  // What the last cut made, kept on the section: a toast is gone before the
  // person has read a notice that matters.
  const [made, setMade] = useState<{ name: string; notice: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setList(await api.baselines(slug))
      setError(null)
    } catch (refused) {
      setError(refused as ApiError)
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    void load()
  }, [load])

  /** Post the cut; `at` is set only after the person chose between two versions. */
  const cut = async (at?: 'imported' | 'current') => {
    setBusy(true)
    try {
      const body: { notes?: string; at?: 'imported' | 'current' } = {}
      if (notes.trim()) body.notes = notes.trim()
      if (at) body.at = at
      const answer = await api.cutBaseline(slug, body)
      if ('moved' in answer) {
        setMoved(answer.moved)
        return
      }
      toasts.succeeded(`Baseline ${answer.cut.name} cut`)
      setMade({ name: answer.cut.name, notice: answer.cut.notice })
      setCutting(false)
      setMoved(null)
      setNotes('')
      await load()
    } catch (failure) {
      // GitLab's own words, and the dialog stays open with the notes in it: a
      // refused cut loses nothing the person typed.
      toasts.failed((failure as ApiError).message || 'GitLab refused')
      setMoved(null)
    } finally {
      setBusy(false)
    }
  }

  // The comparison panel, when one is open. `opened` counts the requests so a second
  // click on Compare starts a fresh panel on its own baseline rather than keeping the
  // sides of the last one.
  const [comparing, setComparing] = useState<{ base: string; head: string; opened: number } | null>(
    null,
  )

  /** Open the baseline if it is not open, wait for it, and make it the current view. */
  const view = async (tag: string) => {
    const status = await opening.open(tag)
    if (status?.state === 'ready') await baselineView.view(tag)
  }

  const items = list?.items ?? []
  const viewedTag = baselineTag(branch)

  return (
    <>
    <Paper sx={{ p: 2 }} data-testid="baselines-section">
      <Stack direction="row" spacing={1.5} sx={{ mb: 1, alignItems: 'center' }}>
        <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
          Baselines
        </Typography>
        {loading ? <CircularProgress size={16} aria-label="Loading the baselines" /> : null}
        {canCut ? (
          <Button variant="outlined" size="small" onClick={() => setCutting(true)}>
            Cut baseline
          </Button>
        ) : null}
      </Stack>

      {made ? (
        <Alert severity={made.notice ? 'info' : 'success'} sx={{ mb: 1.5 }} onClose={() => setMade(null)}>
          Baseline {made.name} was cut.{made.notice ? ` ${made.notice}` : ''}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Could not read the baselines: {error.message}
        </Alert>
      ) : null}

      {list && items.length === 0 && !error ? (
        <Typography variant="body2" color="text.secondary">
          No baselines yet. A baseline is a named, permanent marker on {target}: a protected tag
          that fixes the program as it stood at one moment, so it can be found and compared later.
          {list.prefix ? ` Baseline tags start with ${list.prefix}.` : ''}
        </Typography>
      ) : null}

      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
        {items.map((baseline) => (
          <BaselineRow
            key={baseline.name}
            slug={slug}
            baseline={baseline}
            viewing={viewedTag === baseline.name}
            opening={opening}
            onView={(tag) => void view(tag)}
            onCompare={(tag) =>
              setComparing((was) => ({ base: tag, head: target, opened: (was?.opened ?? 0) + 1 }))
            }
          />
        ))}
      </Stack>
      {list?.truncated ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Only the newest baselines are listed here; GitLab has more.
        </Typography>
      ) : null}

      <Dialog
        open={cutting && !moved}
        onClose={busy ? undefined : () => setCutting(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cut a baseline</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            Baselines are cut from {target}, from the version you are looking at. BEST names the tag.
            It is a protected tag in GitLab, so it cannot be removed from BEST, and GitLab decides
            whether you may create it.
          </DialogContentText>
          <TextField
            label="Notes"
            fullWidth
            multiline
            minRows={3}
            maxRows={10}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={busy}
            helperText="What this baseline is for. Kept in the tag, and cannot be edited later."
            slotProps={{ htmlInput: { maxLength: MAX_NOTES } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCutting(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void cut()} disabled={busy}>
            Cut the baseline
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(moved)} maxWidth="sm" fullWidth>
        <DialogTitle>{target} has moved since you last imported it</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {moved?.detail ||
              `Someone has merged into ${target} since your copy was read. Choose which version the baseline marks.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setMoved(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => void cut('imported')}
            disabled={busy}
          >
            {`Tag what I was looking at (${short(moved?.imported_sha ?? '')})`}
          </Button>
          <Button
            variant="contained"
            onClick={() => void cut('current')}
            disabled={busy}
          >
            {`Tag ${target} as it is now (${short(moved?.current_sha ?? '')})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
    {comparing ? (
      <BaselineCompare
        key={comparing.opened}
        slug={slug}
        target={target}
        names={items.map((baseline) => baseline.name)}
        initial={comparing}
        opening={opening}
        onOpened={() => void load()}
        onClose={() => setComparing(null)}
      />
    ) : null}
    </>
  )
}
